import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import WhatsAppButton from '@/components/layout/WhatsAppButton';
import Hero from '@/components/sections/Hero';
import TestimonialsSection from '@/components/sections/TestimonialsSection';
import FAQSection from '@/components/sections/FAQSection';
import ContactForm from '@/components/sections/ContactForm';
import { SERVICES_FR, SERVICES_EN, CITIES, SITE_CONFIG } from '@/data/siteData';
import { Check, MapPin, ArrowRight, Phone, Clock, Shield, Star } from 'lucide-react';

// Mapping for URL parsing
const SERVICE_URL_MAP_FR = {
  'nettoyage-appartement': 'nettoyage-appartement',
  'nettoyage-villa': 'nettoyage-villa',
  'menage-airbnb': 'menage-airbnb',
  'nettoyage-apres-chantier': 'nettoyage-apres-chantier',
  'nettoyage-fin-location': 'nettoyage-fin-location',
  'conciergerie-airbnb': 'conciergerie-airbnb',
};

const SERVICE_URL_MAP_EN = {
  'apartment-cleaning': 'apartment-cleaning',
  'villa-cleaning': 'villa-cleaning',
  'airbnb-cleaning': 'airbnb-cleaning',
  'post-construction-cleaning': 'post-construction-cleaning',
  'end-of-rental-cleaning': 'end-of-rental-cleaning',
  'airbnb-concierge': 'airbnb-concierge',
};

const parseServiceCitySlug = (fullSlug, lang) => {
  if (!fullSlug) return { serviceSlug: null, citySlug: null };
  
  const serviceMap = lang === 'en' ? SERVICE_URL_MAP_EN : SERVICE_URL_MAP_FR;
  
  // Sort by key length descending to match longer patterns first
  const sortedEntries = Object.entries(serviceMap).sort((a, b) => b[0].length - a[0].length);
  
  for (const [key, serviceSlug] of sortedEntries) {
    if (fullSlug.startsWith(key + '-')) {
      const citySlug = fullSlug.replace(key + '-', '');
      return { serviceSlug, citySlug };
    }
  }
  return { serviceSlug: null, citySlug: null };
};

const CityServicePage = () => {
  const { lang, t } = useLanguage();
  const path = window.location.pathname;
  
  const services = lang === 'en' ? SERVICES_EN : SERVICES_FR;
  const prefix = lang === 'en' ? '/en' : '';
  
  // Parse service and city from URL path
  const parseFromPath = () => {
    const cleanPath = path.replace('/en/', '/').replace(/^\//, '');
    const serviceMap = lang === 'en' ? SERVICE_URL_MAP_EN : SERVICE_URL_MAP_FR;
    
    // Sort by key length descending to match longer patterns first
    const sortedEntries = Object.entries(serviceMap).sort((a, b) => b[0].length - a[0].length);
    
    for (const [key, serviceSlug] of sortedEntries) {
      if (cleanPath.startsWith(key + '-')) {
        const citySlug = cleanPath.replace(key + '-', '');
        return { serviceSlug, citySlug };
      }
    }
    return { serviceSlug: null, citySlug: null };
  };
  
  const { serviceSlug, citySlug } = parseFromPath();
  const service = services.find(s => s.slug === serviceSlug);
  const city = CITIES.find(c => c.slug === citySlug);

  if (!service || !city) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-slate-800 mb-4">
              {t('Page non trouvée', 'Page not found')}
            </h1>
            <Link to={`${prefix}/`} className="text-amber-600 hover:text-amber-700">
              {t("Retour à l'accueil", 'Back to home')}
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const cityName = lang === 'en' ? city.nameEn : city.nameFr;
  
  const pageTitle = lang === 'en'
    ? `${service.title} in ${cityName} | VS Riviera Conciergerie`
    : `${service.title} à ${cityName} | VS Riviera Conciergerie`;

  const heroTitle = lang === 'en'
    ? `${service.title} in ${cityName}`
    : `${service.title} à ${cityName}`;

  const description = lang === 'en'
    ? `Professional ${service.title.toLowerCase()} services in ${cityName}, French Riviera. Fast, reliable, and affordable. Get a free quote today!`
    : `Service professionnel de ${service.title.toLowerCase()} à ${cityName}, Côte d'Azur. Rapide, fiable et abordable. Devis gratuit !`;

  const heroDescription = lang === 'en'
    ? `${service.longDescription} We serve ${cityName} and surrounding areas with premium quality service.`
    : `${service.longDescription} Nous intervenons à ${cityName} et ses environs avec un service de qualité premium.`;

  // Benefits specific to the page
  const benefits = lang === 'en' 
    ? [
        { icon: Clock, title: 'Fast Response', description: 'We respond within 24 hours for all requests' },
        { icon: Shield, title: 'Satisfaction Guaranteed', description: '100% satisfaction or we come back free' },
        { icon: Star, title: 'Experienced Team', description: 'Trained and vetted professionals' },
      ]
    : [
        { icon: Clock, title: 'Réponse Rapide', description: 'Nous répondons sous 24h pour toutes les demandes' },
        { icon: Shield, title: 'Satisfaction Garantie', description: '100% satisfait ou nous repassons gratuitement' },
        { icon: Star, title: 'Équipe Expérimentée', description: 'Professionnels formés et vérifiés' },
      ];

  // Generate page slug for canonical URL
  const pageSlug = `${serviceSlug}-${citySlug}`;

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <link rel="canonical" href={`${window.location.origin}${prefix}/${pageSlug}`} />
      </Helmet>

      <Header />
      
      <main data-testid={`city-service-page-${pageSlug}`}>
        <Hero 
          subtitle={SITE_CONFIG.companyName}
          title={heroTitle}
          description={heroDescription}
          cityName={cityName}
        />

        {/* Service Details */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center p-6">
                  <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-lg text-slate-800 mb-2">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Left - Service Features */}
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-8">
                  {t(`${service.title} à ${cityName}`, `${service.title} in ${cityName}`)}
                </h2>
                
                <div className="prose prose-slate max-w-none mb-8">
                  <p className="text-slate-600 leading-relaxed">
                    {lang === 'en' 
                      ? `Looking for professional ${service.title.toLowerCase()} in ${cityName}? VS Riviera Conciergerie offers premium cleaning services tailored to your needs. Our experienced team ensures impeccable results every time.`
                      : `Vous recherchez un service de ${service.title.toLowerCase()} professionnel à ${cityName} ? VS Riviera Conciergerie vous propose des services de nettoyage premium adaptés à vos besoins. Notre équipe expérimentée garantit des résultats impeccables à chaque intervention.`
                    }
                  </p>
                </div>

                <div className="space-y-4">
                  {service.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{feature}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="mt-8 p-6 bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl text-white">
                  <h3 className="font-bold text-lg mb-2">
                    {t(`${service.title} à ${cityName}`, `${service.title} in ${cityName}`)}
                  </h3>
                  <p className="text-amber-100 mb-4">
                    {t('Contactez-nous pour un devis gratuit', 'Contact us for a free quote')}
                  </p>
                  <a 
                    href={SITE_CONFIG.phoneLink}
                    className="inline-flex items-center gap-2 bg-white text-amber-600 px-6 py-3 rounded-full font-semibold hover:bg-amber-50 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    {SITE_CONFIG.phone}
                  </a>
                </div>
              </div>

              {/* Right - Other Services in City & Other Cities */}
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-8">
                  {t(`Autres services à ${cityName}`, `Other services in ${cityName}`)}
                </h2>
                
                <div className="grid grid-cols-1 gap-3 mb-12">
                  {services.filter(s => s.id !== service.id).map((s) => {
                    const otherServiceLink = lang === 'en'
                      ? `${prefix}/${s.slug}-${city.slug}`
                      : `/${s.slug}-${city.slug}`;
                    
                    return (
                      <Link
                        key={s.id}
                        to={otherServiceLink}
                        className="group flex items-center justify-between p-4 bg-slate-50 hover:bg-amber-50 rounded-xl transition-colors"
                      >
                        <span className="font-medium text-slate-700 group-hover:text-amber-700">
                          {s.title}
                        </span>
                        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-amber-600" />
                      </Link>
                    );
                  })}
                </div>

                <h3 className="text-xl font-bold text-slate-800 mb-6">
                  {t(`${service.title} dans d'autres villes`, `${service.title} in other cities`)}
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  {CITIES.filter(c => c.id !== city.id).map((c) => {
                    const otherCityLink = lang === 'en'
                      ? `${prefix}/${service.slug}-${c.slug}`
                      : `/${service.slug}-${c.slug}`;
                    
                    return (
                      <Link
                        key={c.id}
                        to={otherCityLink}
                        className="group flex items-center gap-2 p-3 bg-slate-50 hover:bg-amber-50 rounded-lg transition-colors"
                      >
                        <MapPin className="w-4 h-4 text-slate-400 group-hover:text-amber-600" />
                        <span className="text-sm text-slate-600 group-hover:text-amber-700">
                          {lang === 'en' ? c.nameEn : c.nameFr}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        <TestimonialsSection />
        <ContactForm />
        <FAQSection />
      </main>

      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default CityServicePage;
